import type { Dayjs } from "dayjs";
import { getAdminNow } from "../../../utils/dateService";

import {
  EARLY_PAYOUT_DOWNLOAD_FILENAME,
  SOCCER_TOTAL_LINE_MIN,
  SOCCER_TOTAL_LINE_STEP,
  SOCCER_TOTAL_MATCH_MAX_MINUTES,
} from "./earlyPayoutConstants";
import type {
  EarlyPayoutRuleLinePayload,
  EarlyPayoutRuleType,
  EarlyPayoutRuleset,
  EarlyPayoutSelector,
  EarlyPayoutSoccerTotalCondition,
} from "./earlyPayoutTypes";

export function getEarlyPayoutRulesetMarketLabel(
  ruleset: Pick<EarlyPayoutRuleset, "marketDisplayName" | "market">,
): string {
  const displayName =
    typeof ruleset.marketDisplayName === "string"
      ? ruleset.marketDisplayName.trim()
      : "";
  if (displayName) {
    return displayName;
  }
  return typeof ruleset.market === "string" ? ruleset.market.trim() : "";
}

export function normalizeEarlyPayoutRuleType(
  value: unknown,
): EarlyPayoutRuleType {
  const normalized =
    typeof value === "string" ? value.trim().toUpperCase() : "";
  if (normalized === "TOTAL") {
    return "TOTAL";
  }
  if (normalized === "TO_QUALIFY") {
    return "TO_QUALIFY";
  }
  return "WINNER";
}

export function getEarlyPayoutEventsQueryDateRange(asOf: Dayjs = getAdminNow()): {
  start_date: string;
  end_date: string;
} {
  return {
    start_date: asOf.format("YYYY-MM-DD"),
    end_date: asOf.add(4, "day").format("YYYY-MM-DD"),
  };
}

export function formatEarlyPayoutMarketLabel(
  marketId?: string,
  marketName?: string,
): string {
  const id = typeof marketId === "string" ? marketId.trim() : "";
  const name = typeof marketName === "string" ? marketName.trim() : "";

  if (id && name) {
    return `ID: ${id} - ${name}`;
  }
  if (id) {
    return `ID: ${id}`;
  }
  return name;
}

export function normalizeEarlyPayoutAdvantage(
  value: unknown,
  fallback = 0,
): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.trunc(parsed);
}

export const EARLY_PAYOUT_SOCCER_TOTAL_TIME_PATTERN = /^\d{2}:\d{2}$/;

export function parseEarlyPayoutSoccerTotalTimeToSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (!EARLY_PAYOUT_SOCCER_TOTAL_TIME_PATTERN.test(trimmed)) {
    return null;
  }

  const [minutes, seconds] = trimmed.split(":").map(Number);
  return minutes * 60 + seconds;
}

export function formatEarlyPayoutSoccerTotalTimeInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) {
    return digits;
  }
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

export function isValidEarlyPayoutSoccerTotalTime(value: unknown): boolean {
  if (value === undefined || value === null || value === "") {
    return true;
  }

  if (typeof value !== "string") {
    return false;
  }

  const trimmed = value.trim();
  if (!EARLY_PAYOUT_SOCCER_TOTAL_TIME_PATTERN.test(trimmed)) {
    return false;
  }

  const [minutes, seconds] = trimmed.split(":").map(Number);
  if (
    !Number.isInteger(minutes) ||
    !Number.isInteger(seconds) ||
    minutes < 0 ||
    minutes > SOCCER_TOTAL_MATCH_MAX_MINUTES ||
    seconds < 0 ||
    seconds > 59
  ) {
    return false;
  }

  if (minutes === SOCCER_TOTAL_MATCH_MAX_MINUTES && seconds > 0) {
    return false;
  }

  return true;
}

export function doEarlyPayoutSoccerTotalTimeRangesOverlap(
  startA: number,
  endA: number,
  startB: number,
  endB: number,
): boolean {
  return startA < endB && startB < endA;
}

export function getOverlappingSoccerTotalConditionIndexes(
  conditions: EarlyPayoutSoccerTotalCondition[],
): [number, number] | null {
  const parsedRanges = conditions.map((condition) => {
    const timeStart =
      typeof condition.timeStart === "string" ? condition.timeStart.trim() : "";
    const timeEnd =
      typeof condition.timeEnd === "string" ? condition.timeEnd.trim() : "";

    if (
      !timeStart ||
      !timeEnd ||
      !isValidEarlyPayoutSoccerTotalTime(timeStart) ||
      !isValidEarlyPayoutSoccerTotalTime(timeEnd) ||
      !isEarlyPayoutSoccerTotalTimeRangeValid(timeStart, timeEnd)
    ) {
      return null;
    }

    const startSeconds = parseEarlyPayoutSoccerTotalTimeToSeconds(timeStart);
    const endSeconds = parseEarlyPayoutSoccerTotalTimeToSeconds(timeEnd);

    if (startSeconds === null || endSeconds === null) {
      return null;
    }

    return { startSeconds, endSeconds };
  });

  for (let indexA = 0; indexA < parsedRanges.length; indexA += 1) {
    const rangeA = parsedRanges[indexA];
    if (!rangeA) {
      continue;
    }

    for (let indexB = indexA + 1; indexB < parsedRanges.length; indexB += 1) {
      const rangeB = parsedRanges[indexB];
      if (!rangeB) {
        continue;
      }

      if (
        doEarlyPayoutSoccerTotalTimeRangesOverlap(
          rangeA.startSeconds,
          rangeA.endSeconds,
          rangeB.startSeconds,
          rangeB.endSeconds,
        )
      ) {
        return [indexA, indexB];
      }
    }
  }

  return null;
}

export function isEarlyPayoutSoccerTotalTimeRangeValid(
  timeStart: unknown,
  timeEnd: unknown,
): boolean {
  if (
    !isValidEarlyPayoutSoccerTotalTime(timeStart) ||
    !isValidEarlyPayoutSoccerTotalTime(timeEnd)
  ) {
    return true;
  }

  const start = typeof timeStart === "string" ? timeStart.trim() : "";
  const end = typeof timeEnd === "string" ? timeEnd.trim() : "";

  if (!start || !end) {
    return true;
  }

  const startSeconds = parseEarlyPayoutSoccerTotalTimeToSeconds(start);
  const endSeconds = parseEarlyPayoutSoccerTotalTimeToSeconds(end);

  if (startSeconds === null || endSeconds === null) {
    return true;
  }

  return endSeconds > startSeconds;
}

export function roundSoccerTotalLineValue(value: number): number {
  return Math.round(value * 100) / 100;
}

export function formatSoccerTotalLineValue(lineValue: number): string {
  const rounded = roundSoccerTotalLineValue(lineValue);
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return rounded.toFixed(2).replace(/0+$/, "");
}

export function formatSoccerTotalLineLabel(lineValue: number): string {
  return `Over ${formatSoccerTotalLineValue(lineValue)}`;
}

export function getSoccerTotalLineValues(
  conditions: Pick<EarlyPayoutSoccerTotalCondition, "lineValue">[],
): number[] {
  return conditions.map((condition) =>
    roundSoccerTotalLineValue(Number(condition.lineValue) || 0),
  );
}

export function canAdjustSoccerTotalLineValue(
  currentValue: number,
  delta: number,
  existingValues: number[],
  excludeIndex?: number,
): boolean {
  const next = roundSoccerTotalLineValue(currentValue + delta);
  if (next < SOCCER_TOTAL_LINE_MIN) {
    return false;
  }

  return !existingValues.some(
    (value, index) => index !== excludeIndex && value === next,
  );
}

export function getNextAvailableSoccerTotalLineValue(
  existingValues: number[],
): number {
  const lastValue =
    existingValues.length > 0
      ? existingValues[existingValues.length - 1]
      : SOCCER_TOTAL_LINE_MIN - SOCCER_TOTAL_LINE_STEP;
  let candidate = roundSoccerTotalLineValue(lastValue + SOCCER_TOTAL_LINE_STEP);

  while (existingValues.includes(candidate)) {
    candidate = roundSoccerTotalLineValue(candidate + SOCCER_TOTAL_LINE_STEP);
  }

  return Math.max(candidate, SOCCER_TOTAL_LINE_MIN);
}

export function mapSoccerTotalConditionsToRuleLines(
  conditions: EarlyPayoutSoccerTotalCondition[] | undefined,
): EarlyPayoutRuleLinePayload[] {
  if (!Array.isArray(conditions)) {
    return [];
  }

  return conditions.map((condition) => ({
    line: roundSoccerTotalLineValue(Number(condition.lineValue)),
    time_start:
      typeof condition.timeStart === "string" ? condition.timeStart.trim() : "",
    time_end: typeof condition.timeEnd === "string" ? condition.timeEnd.trim() : "",
  }));
}

export function mapRuleLinesApiToSoccerTotalConditions(
  lines: EarlyPayoutRuleLinePayload[] | undefined,
): EarlyPayoutSoccerTotalCondition[] {
  if (!Array.isArray(lines)) {
    return [];
  }

  return lines.map((line) => ({
    lineValue: roundSoccerTotalLineValue(Number(line.line)),
    timeStart: typeof line.time_start === "string" ? line.time_start.trim() : "",
    timeEnd: typeof line.time_end === "string" ? line.time_end.trim() : "",
  }));
}

export function escapeCsvCell(value: string | number): string {
  const serialized = String(value);
  if (/[",\n\r]/.test(serialized)) {
    return `"${serialized.replace(/"/g, '""')}"`;
  }
  return serialized;
}

export function buildEarlyPayoutRulesetsCsv(
  rows: EarlyPayoutRuleset[],
): string {
  const header = ["Rule Name", "Rule Type", "Sport", "Market", "Advantage"];
  const lines = [header.map((label) => escapeCsvCell(label)).join(",")];

  for (const row of rows) {
    lines.push(
      [
        escapeCsvCell(row.ruleName),
        escapeCsvCell(row.ruleType),
        escapeCsvCell(row.sport),
        escapeCsvCell(getEarlyPayoutRulesetMarketLabel(row)),
        escapeCsvCell(row.advantage),
      ].join(","),
    );
  }

  return lines.join("\n");
}

export function buildEarlyPayoutSelectorsCsv(
  rows: EarlyPayoutSelector[],
): string {
  const header = ["Selector", "ID", "Status", "Rule Type"];
  const lines = [header.map((label) => escapeCsvCell(label)).join(",")];

  for (const row of rows) {
    lines.push(
      [
        escapeCsvCell(row.selector),
        escapeCsvCell(row.details),
        escapeCsvCell(row.status),
        escapeCsvCell(
          row.rule ? normalizeEarlyPayoutRuleType(row.rule.ruleType) : "",
        ),
      ].join(","),
    );
  }

  return lines.join("\n");
}

export function triggerClientCsvFileDownload(
  csv: string,
  filename = EARLY_PAYOUT_DOWNLOAD_FILENAME,
): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
